import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    AcknowledgementDto,
    AttachmentHandler,
    AttachmentVersionDto,
    CurrentAttachmentVersionDto,
    FlyFreelyError,
    FlyFreelyLoggingService,
    PersonService,
    PersonWithRoles,
    UserService
} from '@flyfreely-portal-ui/flyfreely';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from '@flyfreely-portal-ui/ng-select';
import { personSearch } from '@flyfreely-portal-ui/ui';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { debounceTime, forkJoin, Subject, takeUntil } from 'rxjs';

/**
 * Houses a displayable combination of the attachment version and acknowledgement information
 * for that version.
 */
interface AttachmentVersionsWithAcknowledgement {
    id: number;
    originalFileName?: string;
    // Acknowledgement parameters:
    acknowledgementPersonId?: number;
    acknowledgementTime?: string;
}

/**
 * The subset of person fields needed to display and select a person in the personnel ng-select.
 */
type SelectablePerson = Pick<
    PersonWithRoles,
    'id' | 'firstName' | 'lastName' | 'email'
>;

@Component({
    selector: 'acknowledgement-history',
    templateUrl: './acknowledgement-history.component.html'
})
export class AcknowledgementHistory implements OnInit, OnDestroy {
    @Input() attachment!: CurrentAttachmentVersionDto;
    @Input() attachmentsHandler!: AttachmentHandler;
    @Input() organisationId!: number;
    @Input() canViewOthers = false;
    @Input() canDownload = false;

    acknowledgementHistory: AcknowledgementDto[] = [];
    versionsWithAcknowledgement: AttachmentVersionsWithAcknowledgement[] = [];
    versionHistory: AttachmentVersionDto[] = [];

    /**
     * The person whose acknowledgement history is currently being displayed. Defaults to the
     * current user, and can be changed to another organisation member when `canViewOthers` is
     * `true`.
     */
    selectedPersonId: number | undefined;
    personnel: SelectablePerson[] = [];
    loadingPersonnel = false;
    personSearch = personSearch.bind(this);
    personSelectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: null,
        searchValue: null,
        totalItems: null
    };

    private personSearchSource = new Subject<NgSelectSearch>();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private modal: BsModalRef,
        private personService: PersonService,
        private userService: UserService,
        private logging: FlyFreelyLoggingService
    ) {}

    ngOnInit() {
        const currentUser = this.userService.getCurrentUser();
        this.selectedPersonId = currentUser?.id;
        // Seed the personnel list with the current user so they're selectable/displayable
        // immediately, without waiting on the personnel GraphQL query.
        this.personnel = currentUser
            ? [
                  {
                      id: currentUser.id,
                      firstName: currentUser.firstName,
                      lastName: currentUser.lastName,
                      email: currentUser.email
                  }
              ]
            : [];

        this.personSearchSource
            .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
            .subscribe(search => {
                this.personSelectConfig = ngSelectPreSearchConfigure(
                    search,
                    this.personSelectConfig
                );
                this.refreshPersonnel();
            });

        this.refreshHistory();
    }

    ngOnDestroy() {
        this.personSearchSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    get selectedPerson(): SelectablePerson | undefined {
        return this.personnel.find(
            person => person.id === this.selectedPersonId
        );
    }

    /**
     * Loads the attachment's version history along with the acknowledgement history for the
     * currently selected person, then combines them for display.
     */
    refreshHistory() {
        forkJoin([
            this.attachmentsHandler.findAcknowledgementHistory(
                this.attachment.id,
                this.selectedPersonId
            ),
            this.attachmentsHandler.findAttachmentHistory(
                this.attachment.id,
                this.organisationId
            )
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: ([acknowledgeHistory, versionHistory]) => {
                    this.acknowledgementHistory = acknowledgeHistory ?? [];
                    this.versionHistory = versionHistory.versions ?? [];
                    this.buildAttachmentsWithAcknowledgements();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while getting acknowledgement history: ${error.message}`
                    )
            });
    }

    /**
     * Called when a different person is selected in the ng-select. Only the acknowledgement
     * history needs to be re-fetched, the attachment's version history is independent of the
     * selected person.
     */
    onPersonChange() {
        this.attachmentsHandler
            .findAcknowledgementHistory(
                this.attachment.id,
                this.selectedPersonId
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: acknowledgeHistory => {
                    this.acknowledgementHistory = acknowledgeHistory ?? [];
                    this.buildAttachmentsWithAcknowledgements();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while getting acknowledgement history: ${error.message}`
                    )
            });
    }

    buildAttachmentsWithAcknowledgements() {
        this.versionsWithAcknowledgement = this.versionHistory.map(version => {
            const acknowledgement = this.acknowledgementHistory.find(
                a => a.attachmentVersionId === version.id
            );
            return {
                id: version.id,
                originalFileName: version.originalFileName,
                acknowledgementPersonId: acknowledgement?.personId,
                acknowledgementTime: acknowledgement?.modificationTime
            };
        });
    }

    onPersonSelectOpen() {
        if (
            this.personSelectConfig.totalItems == null ||
            this.personnel.length !== this.personSelectConfig.totalItems
        ) {
            this.refreshPersonnel();
        }
    }

    onPersonSelectScroll() {
        const resultLength =
            this.personSelectConfig.searchValue == null
                ? this.personnel.length
                : this.personnel.filter(person =>
                      this.personSearch(
                          this.personSelectConfig.searchValue,
                          person
                      )
                  ).length;

        if (
            this.personSelectConfig.totalItems == null ||
            resultLength !== this.personSelectConfig.totalItems
        ) {
            this.refreshPersonnel();
        }
    }

    onPersonSelectSearch(search: NgSelectSearch) {
        this.personSearchSource.next(search);
    }

    onPersonSelectClose() {
        this.personSelectConfig.searchValue = null;
    }

    private refreshPersonnel() {
        this.loadingPersonnel = true;
        this.personService
            .findPersonnelGraphQL(
                this.organisationId,
                this.personSelectConfig.currentPage,
                15,
                {
                    textSearch: this.personSelectConfig.searchValue || undefined
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    const newPersonnel = result.results.filter(
                        person =>
                            this.personnel.findIndex(
                                p => p.id === person.id
                            ) === -1
                    );
                    this.personnel = this.personnel.concat(newPersonnel);
                    this.personSelectConfig = updateNgSelectData(
                        result,
                        this.personSelectConfig
                    );
                    this.loadingPersonnel = false;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while loading personnel: ${error.message}`
                    );
                    this.loadingPersonnel = false;
                }
            });
    }

    close() {
        this.modal.hide();
    }
}
